import { createFileRoute, Link } from "@tanstack/react-router";
import { MarketingShell } from "@/components/site/marketing-shell";
import { Button } from "@/components/ui/button";
import { BtcTicker } from "@/components/site/btc-ticker";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";
import { Shield, TrendingUp, Zap, Users, ArrowRight, Star } from "lucide-react";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "CryptoBull Pro — Premium Crypto Investment Platform" },
      { name: "description", content: "Tiered Bitcoin investment plans with up to 5% daily ROI, live dashboard, referral program. Demonstration template." },
      { property: "og:title", content: "CryptoBull Pro — Crypto Investment Platform" },
      { property: "og:description", content: "Premium dark-themed crypto investment demo with admin tools." },
    ],
  }),
  component: Home,
});

const stats = [
  { label: "Active Investors", value: 18420, suffix: "+" },
  { label: "Total Invested", value: 142, suffix: "M+", prefix: "$" },
  { label: "Payouts Processed", value: 96, suffix: "%" },
  { label: "Countries Served", value: 120, suffix: "+" },
];

const planCards = [
  { name: "Starter", roi: "1.5%", duration: "30 days", min: "$500", color: "from-zinc-500 to-zinc-700" },
  { name: "Silver", roi: "2.2%", duration: "60 days", min: "$5,000", color: "from-slate-300 to-slate-500" },
  { name: "Gold", roi: "3.5%", duration: "90 days", min: "$20,000", color: "from-amber-300 to-amber-600" },
  { name: "VIP", roi: "5.0%", duration: "365 days", min: "$100,000", color: "from-primary to-amber-400" },
];

const testimonials = [
  { name: "Marcus T.", role: "Gold Investor", body: "Switched from spreadsheets to CryptoBull Pro and never looked back. Clean UI, real-time data." },
  { name: "Aisha K.", role: "VIP Member", body: "The dashboard is the cleanest I've seen. Withdrawals are processed quickly through the admin panel." },
  { name: "Diego R.", role: "Silver Investor", body: "Referral program actually pays. The breakdown of every transaction makes everything transparent." },
];

const faqs = [
  { q: "How do daily returns work?", a: "Each plan has a fixed daily ROI applied for the plan duration. Earnings accrue automatically and can be withdrawn (in this demo, via admin approval)." },
  { q: "How does the referral program work?", a: "Share your unique referral code. When someone signs up and funds an investment, you earn a 5% commission on their deposits." },
  { q: "Is my data secure?", a: "Authentication and database access are protected by Row-Level Security. Wallet addresses are stored encrypted at rest." },
];

const portfolioBars = Array.from({ length: 32 }, (_, i) =>
  30 + Math.sin(i / 2.5) * 25 + ((i * 37) % 15),
);

function Counter({ to, prefix = "", suffix = "" }: { to: number; prefix?: string; suffix?: string }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const dur = 1400;
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(to * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return <span className="font-mono">{prefix}{n.toLocaleString()}{suffix}</span>;
}

function Home() {
  return (
    <MarketingShell>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 pt-20 pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              Live trading desk · simulated
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
              Trade Bitcoin like an <span className="text-gradient-gold">institution</span>.
            </h1>
            <p className="mt-5 text-lg text-muted-foreground max-w-xl">
              Premium-grade dashboard, tiered investment plans, transparent referral program, and a sleek admin suite — all in one demonstration platform.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="text-base">
                <Link to="/auth">Open free account <ArrowRight className="h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-base">
                <Link to="/plans">View plans</Link>
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex -space-x-2">
                {[1,2,3,4].map(i => <div key={i} className="h-7 w-7 rounded-full border-2 border-background bg-gradient-to-br from-primary to-amber-500" />)}
              </div>
              <span>Joined by 18,000+ traders in 120+ countries</span>
            </div>
          </div>
          <div className="space-y-4">
            <BtcTicker />
            <div className="glass rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-muted-foreground">Portfolio Snapshot</div>
                <div className="text-xs text-emerald-400 font-mono">+ 12.4% (24h)</div>
              </div>
              <div className="font-mono text-3xl">$ 124,820.45</div>
              <div className="mt-4 h-20 flex items-end gap-1">
                {portfolioBars.map((h, i) => (
                  <div key={i} className="flex-1 rounded-sm bg-gradient-to-t from-primary/60 to-amber-300/80" style={{ height: `${h}%` }} />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[{i:Shield,l:"RLS Secured"},{i:Zap,l:"Realtime"},{i:TrendingUp,l:"5% Daily ROI"}].map(({i:Ic,l},k)=>(
                <div key={k} className="glass rounded-xl p-3 text-center">
                  <Ic className="h-4 w-4 text-primary mx-auto mb-1" />
                  <div className="text-[11px] text-muted-foreground">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-white/5 bg-background/60">
        <div className="mx-auto max-w-7xl px-4 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-2xl sm:text-3xl font-semibold text-gradient-gold">
                <Counter to={s.value} prefix={s.prefix} suffix={s.suffix} />
              </div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Plans */}
      <section className="mx-auto max-w-7xl px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">Investment plans for every level</h2>
          <p className="mt-3 text-muted-foreground">Four tiers, transparent ROI, no hidden fees.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {planCards.map((p) => (
            <Card key={p.name} className="glass !bg-transparent !border-white/10 p-6">
              <div className={`h-1 w-12 rounded-full bg-gradient-to-r ${p.color} mb-4`} />
              <div className="text-sm text-muted-foreground">{p.name}</div>
              <div className="mt-1 text-3xl font-semibold font-mono">{p.roi}<span className="text-sm text-muted-foreground font-sans">/day</span></div>
              <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                <div className="flex justify-between"><span>Duration</span><span className="font-mono text-foreground">{p.duration}</span></div>
                <div className="flex justify-between"><span>Min deposit</span><span className="font-mono text-foreground">{p.min}</span></div>
                <div className="flex justify-between"><span>Referral</span><span className="font-mono text-foreground">5%</span></div>
              </div>
              <Button asChild className="w-full mt-6" variant="outline"><Link to="/plans">Choose plan</Link></Button>
            </Card>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="mx-auto max-w-7xl px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">Loved by traders worldwide</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {testimonials.map((t) => (
            <div key={t.name} className="glass rounded-2xl p-6">
              <div className="flex gap-1 text-amber-400 mb-3">
                {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="text-sm leading-relaxed">{t.body}</p>
              <div className="mt-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary to-amber-400" />
                <div>
                  <div className="text-sm font-medium">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 py-20">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">Frequently asked questions</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-2">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`f-${i}`} className="glass rounded-xl px-5 !border-white/10">
              <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Contact / CTA */}
      <section className="mx-auto max-w-5xl px-4 py-20">
        <div className="glass rounded-3xl p-10 text-center">
          <Users className="h-8 w-8 text-primary mx-auto mb-3" />
          <h2 className="text-3xl font-semibold tracking-tight">Ready to start trading?</h2>
          <p className="mt-3 text-muted-foreground">Create your free account in under 60 seconds.</p>
          <div className="mt-6 flex justify-center gap-3">
            <Button asChild size="lg"><Link to="/auth">Get started</Link></Button>
            <Button asChild size="lg" variant="outline"><Link to="/support">Contact support</Link></Button>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
